import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bot, Plus, Search, Filter } from 'lucide-react';
import { adminApi, AgentMetadata } from '../../services/admin-api';

export function AIMain() {
  const [agents, setAgents] = useState<AgentMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const result = await adminApi.agents.list({
        page,
        limit: 20,
        type: typeFilter || undefined,
        deploymentStatus: statusFilter || undefined,
        search: searchTerm || undefined,
        sortBy: 'name',
        sortOrder: 'asc',
      });
      setAgents(result.data);
      setTotalPages(result.pagination.totalPages);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgents();
  }, [page, typeFilter, statusFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadAgents();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'production':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'staging':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'development':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'deprecated':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'specialist':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'manager':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200';
      case 'orchestrator':
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200';
      case 'utility':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bot className="w-8 h-8 text-uw-interactive-primary" />
          <h1 className="text-3xl font-bold text-uw-text-primary">AI Agents</h1>
        </div>
        <div className="flex gap-2">
          <Link
            to="/graph"
            className="inline-flex items-center px-4 py-2 bg-uw-surface-elevated text-uw-text-primary border border-uw-border-default rounded-md hover:bg-uw-surface-hover transition-colors"
          >
            <Filter className="w-4 h-4 mr-2" />
            Dependency Graph
          </Link>
          <Link
            to="/ai/new"
            className="inline-flex items-center px-4 py-2 bg-uw-interactive-primary text-white rounded-md hover:bg-uw-interactive-primary-hover transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Agent
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-4 mb-6">
        <form onSubmit={handleSearch} className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-uw-text-secondary mb-1">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-uw-text-tertiary" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search agents..."
                className="w-full pl-10 pr-4 py-2 border border-uw-border-default rounded-md bg-uw-surface-default text-uw-text-primary focus:ring-2 focus:ring-uw-interactive-primary focus:border-transparent"
              />
            </div>
          </div>
          <div className="w-48">
            <label className="block text-sm font-medium text-uw-text-secondary mb-1">
              Type
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-4 py-2 border border-uw-border-default rounded-md bg-uw-surface-default text-uw-text-primary focus:ring-2 focus:ring-uw-interactive-primary focus:border-transparent"
            >
              <option value="">All Types</option>
              <option value="specialist">Specialist</option>
              <option value="manager">Manager</option>
              <option value="orchestrator">Orchestrator</option>
              <option value="utility">Utility</option>
            </select>
          </div>
          <div className="w-48">
            <label className="block text-sm font-medium text-uw-text-secondary mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-uw-border-default rounded-md bg-uw-surface-default text-uw-text-primary focus:ring-2 focus:ring-uw-interactive-primary focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="development">Development</option>
              <option value="staging">Staging</option>
              <option value="production">Production</option>
              <option value="deprecated">Deprecated</option>
            </select>
          </div>
          <button
            type="submit"
            className="px-6 py-2 bg-uw-interactive-primary text-white rounded-md hover:bg-uw-interactive-primary-hover transition-colors"
          >
            Search
          </button>
        </form>
      </div>

      {/* Agent List */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-uw-interactive-primary"></div>
          <p className="mt-4 text-uw-text-secondary">Loading agents...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {!loading && !error && agents.length === 0 && (
        <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-12 text-center">
          <Bot className="w-16 h-16 text-uw-text-tertiary mx-auto mb-4" />
          <p className="text-uw-text-secondary">No agents found</p>
        </div>
      )}

      {!loading && !error && agents.length > 0 && (
        <div className="space-y-4">
          {agents.map((agent) => (
            <Link
              key={agent.id}
              to={`/ai/${agent.id}`}
              className="block bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-6 hover:shadow-lg hover:border-uw-interactive-primary transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-uw-text-primary">
                      {agent.name}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getTypeColor(agent.type)}`}>
                      {agent.type}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(agent.deploymentStatus)}`}>
                      {agent.deploymentStatus}
                    </span>
                  </div>
                  <p className="text-sm text-uw-text-secondary mb-3">
                    {agent.description}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-uw-text-tertiary">
                    <span>Function: {agent.functionName}</span>
                    {agent.modelConfig?.model && (
                      <span>Model: {agent.modelConfig.model}</span>
                    )}
                    <span>Dependencies: {agent.dependencies.length}</span>
                    {agent.tags.length > 0 && (
                      <div className="flex gap-1">
                        {agent.tags.map((tag, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 bg-uw-surface-elevated text-uw-text-secondary rounded text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 border border-uw-border-default rounded-md bg-uw-surface-default text-uw-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-uw-surface-hover transition-colors"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-uw-text-secondary">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 border border-uw-border-default rounded-md bg-uw-surface-default text-uw-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-uw-surface-hover transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
